import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from 'vue';
import ElementLibrarySelector from '@/components/DesignStudio/ElementLibrarySelector.vue';
import ColorOptionsClipboardButtons from '@/components/DesignStudio/ColorOptionsClipboardButtons.vue';
import CSelect, { valueReducer, type CSelectItem } from '@/components/shared/CSelect';
import type { CanvasEx } from '@/typings/canvas';
import type { VectorPlaceholderElement } from '@/typings/customilyElements/canvasElements';
import type { ColorObject } from '@/typings/libraries';
import { LibraryTypeEnum } from '@/utils/enums/libraryType.enum';
import { colorsListStore, useColorOptions } from '@/composables/colorOptions';
import { useColorOptionsClipboard } from '@/composables/useColorOptionsClipboard';
import { useToaster } from '@/composables/toaster';
import { DatadogEvent, fireDatadogLog } from '@/utils/datadogUtils';
import { recordFeatureUsage, TrackedFeature } from '@/utils/featureUsage';


export default /*#__PURE__*/_defineComponent({
  __name: 'VectorColorProperties',
  props: {
    canvas: null,
    vectorElement: null,
    isSolidBackground: { type: Boolean },
    editMode: { type: Boolean },
    disableColorMode: { type: Boolean },
    readonly: { type: Boolean },
    cmykMode: { type: Boolean }
  },
  emits: ["open-color-library-selector"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'open-color-library-selector'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    canvas: CanvasEx;
    vectorElement: VectorPlaceholderElement;
    isSolidBackground?: boolean;
    editMode?: boolean;
    disableColorMode?: boolean;
    readonly?: boolean;
    cmykMode?: boolean;
};

const COLOR_TYPE = LibraryTypeEnum.Color;
const DEFAULT_VECTOR_COLOR: ColorObject = { hex: '#000000', alpha: 1, cmyk: '0,0,0,100' };

const vectorTypeOptions: CSelectItem<boolean>[] = [
    {
        label: 'Full Color',
        value: false
    },
    {
        label: 'Single Color',
        value: true
    }
];





const vectorMode = ref(false);

const disabled = computed(() => !!props.vectorElement?.locked && !props.isSolidBackground);

const colors = useColorOptions<VectorPlaceholderElement>({
    store: colorsListStore((element, css) => element.set('svgColor', css)),
    element: () => props.vectorElement,
    canvas: () => props.canvas,
    editMode: () => props.editMode,
    openLibrarySelector: () => emit('open-color-library-selector')
});

watch(
    () => props.vectorElement,
    (element) => {
        if (!element || element.starMapConfig) return;

        vectorMode.value = element.applyColoring || props.isSolidBackground || false;
    }
);

const updateVectorMode = () => {
    const element = props.vectorElement;
    if (!element) return;

    element.applyColoring = vectorMode.value;
    if (props.editMode) {
        element.setEditedProp('applyColoring');
    }

    if (!vectorMode.value) {
        colors.removeColorLibrary();
        element.set('svgColor', undefined);
        props.canvas.requestRenderAll();
        return;
    }

    // A single color vector needs at least one option to be painted with
    if (!element.colors?.length) {
        colors.replaceColors(new Map([[1, DEFAULT_VECTOR_COLOR]]), element.colorLibraryId);
        return;
    }

    colors.loadColorIntoElement(1);
};

/* Copy/paste color options, shared with Fill/Stroke/Shadow */
const clipboard = useColorOptionsClipboard();
const toast = useToaster();

const colorSectionLabel = computed(() => (props.isSolidBackground ? 'Solid Bg Color' : 'Vector Color'));

const canCopyPasteVectorColors = computed(() => !disabled.value && !props.readonly);
const canPasteVectorColors = computed(() => {
    const clip = clipboard.clip();
    return canCopyPasteVectorColors.value && !!clip && !clip.hasNonSolidColor;
});

const onCopyVectorColors = () => {
    clipboard.copy({
        colors: new Map(colors.model.colorsMap),
        libraryId: colors.model.libraryId
    });
    fireDatadogLog(DatadogEvent.CopyDsColorOptions);
    toast.show(`${colorSectionLabel.value} options copied`);
};

const onPasteVectorColors = () => {
    const clip = clipboard.clip();
    if (!props.vectorElement || !clip || clip.hasNonSolidColor) return;

    colors.replaceColors(clip.colors, clip.libraryId);
    fireDatadogLog(DatadogEvent.PasteDsColorOptions);
    recordFeatureUsage(TrackedFeature.ColorsPasteVector);
    toast.show(`${colorSectionLabel.value} options pasted`);
};

onMounted(() => {
    vectorMode.value = props?.vectorElement?.applyColoring || !!props.isSolidBackground;
    if (vectorMode.value) {
        colors.loadColorIntoElement(1);
    }
});

return { __sfc: true,COLOR_TYPE, DEFAULT_VECTOR_COLOR, vectorTypeOptions, props, emit, vectorMode, disabled, colors, updateVectorMode, clipboard, toast, colorSectionLabel, canCopyPasteVectorColors, canPasteVectorColors, onCopyVectorColors, onPasteVectorColors, ElementLibrarySelector, ColorOptionsClipboardButtons, CSelect, valueReducer }
}

})